import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
  Stack,
  TablePagination,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { utcMs } from '../utils/dateFormat';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AppLayout from '../components/AppLayout';
import { useAuth } from '../contexts/AuthContext';
import { formatReviewStatus, getReviewStatusColor, REVIEW_STATUS_FILTER_OPTIONS } from '../utils/reviewStatus';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad, pagination, permissionCopy } from '../utils/userFacingCopy';
import type { PagedResult } from '../types/pagedResult';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface ReviewListItem {
  id: string;
  reviewCycleId: string;
  reviewCycleName?: string;
  employeeId: string;
  employeeName?: string;
  departmentIds?: string[];
  departmentNames?: string[];
  managerId?: string;
  managerName?: string;
  status: string | number;
  combinedRating?: number;
  excludeRequestedAt?: string;
}

interface DepartmentAverageRow {
  departmentName: string;
  averageCombined: number;
  finalisedCount: number;
  previousAverageCombined?: number;
  delta?: number;
}

interface RatingDistributionRow {
  band: string;
  count: number;
}

interface ReviewCycle {
  id: string;
  name: string;
  startDate: string;
}

type ReviewSortField = 'employeeName' | 'managerName' | 'reviewCycleName' | 'status' | 'combinedRating';

export default function AllReviewsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userRoles = user?.roles ?? [];
  const isAdmin = userRoles.includes('Administrator');
  const isSeniorManager = userRoles.includes('SeniorManager');
  const canAccessAllReviews = isAdmin || isSeniorManager;
  const [searchParams, setSearchParams] = useSearchParams();
  const [reviews, setReviews] = useState<ReviewListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [sortField, setSortField] = useState<ReviewSortField>('employeeName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pagination.auditLogDefaultPageSize);

  const urlDepartmentId = searchParams.get('departmentId');
  const urlDepartmentName = searchParams.get('departmentName');
  const urlStatusParam = searchParams.get('status');
  const validStatusKeys = ['all', ...REVIEW_STATUS_FILTER_OPTIONS.map((o) => o.value)];
  const urlStatusFilter = urlStatusParam && validStatusKeys.includes(urlStatusParam) ? urlStatusParam : 'all';

  const [selectedDepartment, setSelectedDepartment] = useState<string>(urlDepartmentId || urlDepartmentName || 'all');
  const [selectedStatus, setSelectedStatus] = useState<string>(urlStatusFilter);

  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
  const [reviewCycles, setReviewCycles] = useState<ReviewCycle[]>([]);
  const [avgCycleId, setAvgCycleId] = useState<string>('all');
  const [departmentAverages, setDepartmentAverages] = useState<DepartmentAverageRow[]>([]);
  const [ratingDistributionData, setRatingDistributionData] = useState<RatingDistributionRow[]>([]);
  const filtersCardRef = useRef<HTMLDivElement | null>(null);

  const departmentOptions = useMemo(() => {
    const list = [...departments];
    if (selectedDepartment !== 'all' && !list.some((d) => d.id === selectedDepartment)) {
      const byCasing = list.find((d) => d.id.toLowerCase() === selectedDepartment.toLowerCase() || d.name === selectedDepartment);
      list.push({
        id: selectedDepartment,
        name: byCasing?.name ?? urlDepartmentName ?? '—',
      });
    }
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [departments, selectedDepartment, urlDepartmentName]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearchQuery(searchTerm.trim());
    }, 400);
    return () => window.clearTimeout(handle);
  }, [searchTerm]);

  const resolveDepartmentId = useCallback(() => {
    if (selectedDepartment === 'all') return undefined;
    const dept = departments.find(
      (d) =>
        d.id === selectedDepartment
        || d.id.toLowerCase() === selectedDepartment.toLowerCase()
        || d.name === selectedDepartment
    );
    return dept?.id ?? selectedDepartment;
  }, [departments, selectedDepartment]);

  const loadReferenceData = useCallback(async () => {
    const [departmentsResponse, cyclesResponse] = await Promise.all([
      apiService.get<{ id: string; name: string }[]>(API_ENDPOINTS.REFERENCE_DATA.DEPARTMENTS.BASE).catch(() => ({ data: [] })),
      apiService.get<ReviewCycle[]>('/review-cycles').catch(() => ({ data: [] })),
    ]);
    const deptList = [...departmentsResponse.data].sort((a, b) => a.name.localeCompare(b.name));
    setDepartments(deptList);
    const sortedCycles = [...(cyclesResponse.data ?? [])].sort((a, b) => utcMs(a.startDate) - utcMs(b.startDate));
    setReviewCycles(sortedCycles);
  }, []);

  const loadAnalytics = useCallback(async () => {
    if (!canAccessAllReviews) return;
    try {
      setAnalyticsLoading(true);
      const params = new URLSearchParams();
      if (avgCycleId !== 'all') params.set('cycleId', avgCycleId);
      const query = params.toString();
      const response = await apiService.get<{
        departmentAverages: DepartmentAverageRow[];
        ratingDistribution: RatingDistributionRow[];
      }>(`/reviews/all/analytics${query ? `?${query}` : ''}`);
      setDepartmentAverages(response.data.departmentAverages ?? []);
      setRatingDistributionData(response.data.ratingDistribution ?? []);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, couldNotLoad('review analytics')));
    } finally {
      setAnalyticsLoading(false);
    }
  }, [avgCycleId, canAccessAllReviews]);

  const loadReviews = useCallback(async () => {
    if (!canAccessAllReviews) return;
    try {
      setListLoading(true);
      setError('');
      const params = new URLSearchParams();
      params.append('page', (page + 1).toString());
      params.append('pageSize', rowsPerPage.toString());
      params.append('sortBy', sortField);
      params.append('sortDirection', sortDirection);
      if (debouncedSearchQuery) params.append('searchQuery', debouncedSearchQuery);
      const departmentId = resolveDepartmentId();
      if (departmentId) params.append('departmentId', departmentId);
      if (selectedStatus !== 'all') params.append('statusFilter', selectedStatus);

      const response = await apiService.get<PagedResult<ReviewListItem>>(`/reviews/all?${params.toString()}`);
      setReviews(response.data.items);
      setTotalCount(response.data.totalCount);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, couldNotLoad('reviews')));
    } finally {
      setListLoading(false);
      setInitialLoading(false);
    }
  }, [
    canAccessAllReviews,
    page,
    rowsPerPage,
    sortField,
    sortDirection,
    debouncedSearchQuery,
    selectedStatus,
    resolveDepartmentId,
  ]);

  useEffect(() => {
    if (!canAccessAllReviews) {
      setInitialLoading(false);
      return;
    }
    void loadReferenceData();
  }, [canAccessAllReviews, loadReferenceData]);

  useEffect(() => {
    if (!canAccessAllReviews) return;
    void loadAnalytics();
  }, [canAccessAllReviews, loadAnalytics]);

  useEffect(() => {
    if (!canAccessAllReviews) return;
    void loadReviews();
  }, [canAccessAllReviews, loadReviews]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchQuery]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedDepartment !== 'all') {
      const dept = departments.find(
        (d) =>
          d.id === selectedDepartment
          || d.id.toLowerCase() === selectedDepartment.toLowerCase()
          || d.name === selectedDepartment
      );
      params.set('departmentId', dept?.id ?? selectedDepartment);
      if (dept?.name ?? urlDepartmentName) params.set('departmentName', dept?.name ?? urlDepartmentName ?? '');
    }
    if (selectedStatus !== 'all') {
      params.set('status', selectedStatus);
    }
    setSearchParams(params, { replace: true });
  }, [selectedDepartment, selectedStatus, departments, urlDepartmentName, setSearchParams]);

  const handleSort = (field: ReviewSortField) => {
    setPage(0);
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: ReviewSortField) => {
    if (sortField !== field) {
      return <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} />;
    }
    return sortDirection === 'asc'
      ? <ArrowUpwardIcon sx={{ fontSize: 16 }} />
      : <ArrowDownwardIcon sx={{ fontSize: 16 }} />;
  };

  const scrollToFiltersCard = useCallback(() => {
    window.requestAnimationFrame(() => {
      filtersCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const departmentInsight = useMemo(() => {
    const withDelta = departmentAverages.filter((d) => d.delta != null);
    if (withDelta.length === 0) {
      return {
        summary: 'Not enough prior-cycle data yet to show department trend movement.',
        topGainDepartment: null as string | null,
        topGainDelta: 0,
      };
    }
    const improved = withDelta.filter((d) => (d.delta ?? 0) > 0).length;
    const declined = withDelta.filter((d) => (d.delta ?? 0) < 0).length;
    const stable = withDelta.length - improved - declined;
    const topGain = [...withDelta].sort((a, b) => (b.delta ?? 0) - (a.delta ?? 0))[0];
    return {
      summary: `${improved} improved, ${declined} declined, ${stable} stable.`,
      topGainDepartment: topGain.departmentName,
      topGainDelta: topGain.delta ?? 0,
    };
  }, [departmentAverages]);

  const trendHeaderLabel = avgCycleId === 'all'
    ? 'Trend (latest vs prior)'
    : 'Trend (vs previous cycle)';

  const distributionInsight = useMemo(() => {
    const total = ratingDistributionData.reduce((sum, row) => sum + row.count, 0);
    if (total === 0) return 'No finalised ratings available for this filter yet.';
    const sorted = [...ratingDistributionData].sort((a, b) => b.count - a.count);
    const top = sorted[0];
    const pct = Math.round((top.count / total) * 100);
    return `${top.band} is most common (${top.count}/${total}, ${pct}%).`;
  }, [ratingDistributionData]);

  const hasActiveFilters = searchTerm.trim() !== '' || selectedDepartment !== 'all' || selectedStatus !== 'all';

  if (initialLoading) {
    return (
      <AppLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }} aria-busy="true">
          <CircularProgress size={60} />
        </Box>
      </AppLayout>
    );
  }

  if (!canAccessAllReviews) {
    return (
      <AppLayout>
        <Box sx={{ maxWidth: '1400px', mx: 'auto', py: 4 }}>
          <Card variant="outlined" sx={{ p: 3 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {permissionCopy.allReviewsList}
            </Alert>
            <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard/organisation')}>
              Back to Organisation dashboard
            </Button>
          </Card>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto', pb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{ mr: -0.5 }}
              title="Back"
              size="small"
            >
              <ArrowBackIcon />
            </IconButton>
            <AssignmentIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
            <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
              All Reviews
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {totalCount} review{totalCount !== 1 ? 's' : ''}
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          View all performance reviews across the organisation. Filter by department, status, or search for specific reviews.
        </Alert>

        <Card variant="outlined" sx={{ mb: 3, position: 'relative' }}>
          {analyticsLoading && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.6)', zIndex: 1 }}>
              <CircularProgress size={32} />
            </Box>
          )}
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
              <Typography variant="h6" sx={{ mb: 0.5 }}>Department Average Rating</Typography>
              <FormControl sx={{ minWidth: 240 }}>
                <InputLabel>Cycle (for averages)</InputLabel>
                <Select value={avgCycleId} label="Cycle (for averages)" onChange={(e) => setAvgCycleId(e.target.value)}>
                  <MenuItem value="all">All cycles</MenuItem>
                  {reviewCycles.map((cycle) => (
                    <MenuItem key={cycle.id} value={cycle.id}>{cycle.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Average combined rating from finalised reviews only. Trend compares selected cycle against the previous cycle (or latest vs previous when All cycles is selected).
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              {departmentInsight.topGainDepartment ? (
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
                  <Typography variant="body2">{departmentInsight.summary}</Typography>
                  <Typography variant="body2">Largest gain:</Typography>
                  <Link
                    component="button"
                    variant="body2"
                    underline="hover"
                    onClick={() => {
                      const dept = departments.find((d) => d.name === departmentInsight.topGainDepartment);
                      setSelectedDepartment(dept?.id ?? departmentInsight.topGainDepartment!);
                      scrollToFiltersCard();
                    }}
                  >
                    {departmentInsight.topGainDepartment}
                  </Link>
                  <Typography variant="body2">
                    ({departmentInsight.topGainDelta >= 0 ? '+' : ''}{departmentInsight.topGainDelta.toFixed(2)})
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="body2">{departmentInsight.summary}</Typography>
              )}
            </Alert>
            {departmentAverages.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No finalised review ratings available yet.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Department</strong></TableCell>
                      <TableCell align="center"><strong>Finalised Reviews</strong></TableCell>
                      <TableCell align="center"><strong>Avg Combined</strong></TableCell>
                      <TableCell align="center"><strong>{trendHeaderLabel}</strong></TableCell>
                      <TableCell align="right"><strong>Action</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {departmentAverages.slice(0, 8).map((row) => (
                      <TableRow key={row.departmentName}>
                        <TableCell>{row.departmentName}</TableCell>
                        <TableCell align="center">{row.finalisedCount}</TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold" color="primary.main">
                            {row.averageCombined.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {row.delta == null ? (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          ) : (
                            <Typography variant="body2" fontWeight={600} color={row.delta >= 0 ? 'success.main' : 'error.main'}>
                              {row.delta >= 0 ? '▲' : '▼'} {Math.abs(row.delta).toFixed(2)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Link
                            component="button"
                            variant="body2"
                            underline="hover"
                            onClick={() => {
                              const dept = departments.find((d) => d.name === row.departmentName);
                              setSelectedDepartment(dept?.id ?? row.departmentName);
                              scrollToFiltersCard();
                            }}
                          >
                            View reviews
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <Card ref={filtersCardRef} variant="outlined" sx={{ mb: 3, position: 'relative' }}>
          {analyticsLoading && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.6)', zIndex: 1 }}>
              <CircularProgress size={32} />
            </Box>
          )}
          <CardContent>
            <Typography variant="h6" sx={{ mb: 0.5 }}>Final Rating Distribution</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Finalised review outcomes by rating band for the selected cycle filter.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              {distributionInsight}
            </Alert>
            <Box sx={{ width: '100%', height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ratingDistributionData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="band" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#1976d2" name="Reviews" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                sx={{ flex: 1, minWidth: 200 }}
                label="Search reviews"
                placeholder="Search by employee, manager, or review cycle..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Department</InputLabel>
                <Select
                  value={selectedDepartment}
                  label="Department"
                  onChange={(e) => {
                    setPage(0);
                    setSelectedDepartment(e.target.value);
                  }}
                >
                  <MenuItem value="all">All Departments</MenuItem>
                  {departmentOptions.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={selectedStatus}
                  label="Status"
                  onChange={(e) => {
                    setPage(0);
                    setSelectedStatus(e.target.value);
                  }}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  {REVIEW_STATUS_FILTER_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {(selectedDepartment !== 'all' || selectedStatus !== 'all') && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                {selectedDepartment !== 'all' && (() => {
                  const dept = departments.find(
                    (d) =>
                      d.id === selectedDepartment
                      || d.id.toLowerCase() === selectedDepartment.toLowerCase()
                      || d.name === selectedDepartment
                  );
                  const name = dept?.name ?? urlDepartmentName ?? null;
                  return (
                    <Chip
                      label={name != null ? `Department: ${name}` : 'Department filter applied'}
                      color="warning"
                      onDelete={() => setSelectedDepartment('all')}
                      size="small"
                    />
                  );
                })()}
                {selectedStatus !== 'all' && (() => {
                  const opt = REVIEW_STATUS_FILTER_OPTIONS.find((o) => o.value === selectedStatus);
                  const label = opt?.label ?? formatReviewStatus(selectedStatus);
                  return (
                    <Chip
                      label={`Status: ${label}`}
                      color={getReviewStatusColor(selectedStatus)}
                      onDelete={() => setSelectedStatus('all')}
                      size="small"
                    />
                  );
                })()}
              </Box>
            )}
          </CardContent>
        </Card>

        <Card
          sx={{
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderRadius: 2,
            position: 'relative',
          }}
        >
          {listLoading && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.6)', zIndex: 1 }}>
              <CircularProgress size={32} />
            </Box>
          )}
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { opacity: 0.7 },
                        }}
                        onClick={() => handleSort('employeeName')}
                      >
                        <strong>Employee</strong>
                        {getSortIcon('employeeName')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { opacity: 0.7 },
                        }}
                        onClick={() => handleSort('managerName')}
                      >
                        <strong>Manager</strong>
                        {getSortIcon('managerName')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { opacity: 0.7 },
                        }}
                        onClick={() => handleSort('reviewCycleName')}
                      >
                        <strong>Review Cycle</strong>
                        {getSortIcon('reviewCycleName')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { opacity: 0.7 },
                        }}
                        onClick={() => handleSort('status')}
                      >
                        <strong>Status</strong>
                        {getSortIcon('status')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          cursor: 'pointer',
                          userSelect: 'none',
                          '&:hover': { opacity: 0.7 },
                        }}
                        onClick={() => handleSort('combinedRating')}
                      >
                        <strong>Rating</strong>
                        {getSortIcon('combinedRating')}
                      </Box>
                    </TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reviews.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, border: 'none' }}>
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                          <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            {hasActiveFilters ? 'No reviews match your search or filters.' : 'No reviews found.'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    reviews.map((review) => (
                      <TableRow key={review.id} hover>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {review.employeeName || 'Unknown Employee'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {review.managerName || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {review.reviewCycleName || 'Unknown Cycle'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Chip
                              label={formatReviewStatus(review.status)}
                              size="small"
                              color={getReviewStatusColor(review.status)}
                            />
                            {review.excludeRequestedAt && String(review.status).toLowerCase() !== 'excluded' && (
                              <Chip label="Exclude Requested" size="small" color="warning" variant="outlined" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {review.combinedRating !== undefined && review.combinedRating !== null ? (
                            <Typography variant="body2" fontWeight="bold" color="primary.main">
                              {review.combinedRating.toFixed(1)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              N/A
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate(`/reviews/${review.id}`)}
                            sx={{ textTransform: 'none' }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[...pagination.auditLogPageSizeOptions]}
            />
          </CardContent>
        </Card>
      </Box>
    </AppLayout>
  );
}
